import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Dumbbell,
  Target,
  Waves,
  Users,
  BarChart3,
  Wallet,
  Shield,
  Clock,
  Menu,
  X
} from 'lucide-react';

const ForPartnersPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
  };

  const benefits = [
    { icon: <Wallet size={24} color="#ffffff" />, title: 'Zero Cost to Join', desc: 'No listing fees, no monthly charges, no setup cost. You earn from day one.' },
    { icon: <Users size={24} color="#ffffff" />, title: 'New Paying Users', desc: 'Reach fitness enthusiasts and corporate employees who would never walk in otherwise.' },
    { icon: <BarChart3 size={24} color="#ffffff" />, title: 'Fill Empty Slots', desc: 'Turn off-peak hours into revenue. Your existing members are unaffected.' },
    { icon: <BarChart3 size={24} color="#ffffff" />, title: 'Real-Time Dashboard', desc: 'Track bookings, revenue, ratings, and payouts from your Partner Dashboard.' },
    { icon: <Shield size={24} color="#ffffff" />, title: 'Verified Users Only', desc: 'Every HUSLRZ user is phone-verified and rated. Bad actors are banned.' },
    { icon: <Clock size={24} color="#ffffff" />, title: 'Automated Payouts', desc: 'Get paid on time, every time. Bi-weekly or monthly payouts directly to your bank.' },
  ];

  const categories = [
    { icon: <Dumbbell size={20} color="#14B8A6" />, name: 'Gyms & Fitness Centres' },
    { icon: <Target size={20} color="#14B8A6" />, name: 'Yoga & Pilates Studios' },
    { icon: <Waves size={20} color="#14B8A6" />, name: 'Swimming Pools' },
    { icon: <Users size={20} color="#14B8A6" />, name: 'Turf & Sports Courts' },
    { icon: <Users size={20} color="#14B8A6" />, name: 'CrossFit Boxes' },
    { icon: <Users size={20} color="#14B8A6" />, name: 'Dance & Martial Arts Studios' },
  ];

  const howItWorks = [
    { step: '01', title: 'Sign Up', desc: 'Register your facility on the HUSLRZ Partner Dashboard. It takes under 10 minutes.' },
    { step: '02', title: 'Get Verified', desc: 'Our team verifies your facility for safety, hygiene, and amenities. You go live within 48 hours.' },
    { step: '03', title: 'Receive Bookings', desc: 'Users discover your facility on the HUSLRZ app and book available slots.' },
    { step: '04', title: 'Scan & Earn', desc: 'User shows a QR code at your door. You scan it. Payment settles automatically.' },
  ];

  const faqs = [
    { q: 'Is there any cost to list my facility on HUSLRZ?', a: 'No. There is zero cost to join HUSLRZ — no listing fee, no monthly fee, no setup charge. HUSLRZ earns a small commission only when a booking happens.' },
    { q: 'Will HUSLRZ users replace my existing members?', a: 'No. HUSLRZ\'s per-session pricing is higher than typical membership rates per visit. HUSLRZ brings you users who would never buy an annual membership — travellers, explorers, and corporate employees. Some may even convert to your members.' },
    { q: 'How and when do I get paid?', a: 'Payouts are automated — bi-weekly or monthly, directly to your registered bank account. You receive a detailed statement with every payout cycle.' },
    { q: 'Do I control my own pricing?', a: 'Yes. You set your per-session price. HUSLRZ may suggest dynamic pricing for off-peak hours to help fill empty slots, but you always approve.' },
    { q: 'Can I leave HUSLRZ anytime?', a: 'Yes. There is no lock-in. You can delist your facility with just 7 days notice. Active bookings are honoured and then your listing is removed.' },
    { q: 'What about corporate bookings?', a: 'HUSLRZ partners with companies for employee wellness. When a corporate client enrols, their employees get credits to book at your facility — meaning more footfall and guaranteed payments for you.' },
  ];

  return (
    <>
      <Helmet>
        <title>For Partners — List Your Gym, Turf or Studio on HUSLRZ</title>
        <meta name="description" content="Join HUSLRZ for free. Fill empty slots with verified, paying users. Automated payouts, real-time dashboard, and corporate wellness bookings." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>
                  Get Started <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>
                Get Started <ArrowRight size={18} />
              </Link>
            </div>
          </>
        )}

        {/* Hero */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', textAlign: 'center' as const, background: '#ffffff' }}>
          <div style={styles.container}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e6f7f5', color: '#14B8A6', padding: '8px 16px', borderRadius: '24px', fontSize: '14px', fontWeight: 500, marginBottom: '32px' }}>
              <CheckCircle2 size={16} /> Zero cost to join
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 7vw, 56px)', fontWeight: 400, color: '#202124', marginBottom: '24px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
              Your empty slots are<br />worth <span style={{ color: '#14B8A6' }}>real money</span>
            </h1>
            <p style={{ fontSize: isMobile ? '16px' : '20px', color: '#5f6368', lineHeight: 1.6, maxWidth: '650px', margin: '0 auto 40px' }}>
              List your gym, studio, turf or pool on HUSLRZ. We send you verified, paying users.
              You keep the majority. We handle payments, bookings, and support.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/gym-survey" style={{ ...styles.primaryButton, padding: '16px 32px', fontSize: '16px' }}>
                Register Your Facility <ArrowRight size={18} />
              </a>
              <Link to="/contact" style={{ background: '#ffffff', color: '#14B8A6', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '1px solid #dadce0', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Talk to Our Team
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const }}>
              Why partners choose HUSLRZ
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '600px', margin: '0 auto 48px' }}>
              No risk, no cost, just new revenue from your existing capacity
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '20px' }}>
              {benefits.map((b, i) => (
                <div key={i} style={{ background: '#ffffff', borderRadius: '16px', padding: isMobile ? '24px' : '32px', border: '1px solid #e8eaed' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                    {b.icon}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{b.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const }}>
              Get started in 4 simple steps
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '500px', margin: '0 auto 48px' }}>
              From sign-up to your first booking in under 48 hours
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: isMobile ? '24px' : '32px' }}>
              {howItWorks.map((s, i) => (
                <div key={i} style={{ textAlign: 'center' as const }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#e6f7f5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '20px', fontWeight: 700, color: '#14B8A6' }}>
                    {s.step}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{s.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner Categories */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const }}>
              We welcome all fitness & sports facilities
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '550px', margin: '0 auto 40px' }}>
              If you have a space and time slots to offer, HUSLRZ works for you
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px', maxWidth: '800px', margin: '0 auto' }}>
              {categories.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e8eaed' }}>
                  {c.icon}
                  <span style={{ fontSize: '15px', fontWeight: 500, color: '#202124' }}>{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '48px', textAlign: 'center' as const }}>
              Frequently asked questions
            </h2>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: '1px solid #e8eaed', paddingBottom: '24px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#202124', marginBottom: '8px' }}>{faq.q}</h3>
                <p style={{ fontSize: '15px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', textAlign: 'center' as const, color: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 48px)', fontWeight: 400, marginBottom: '16px' }}>
              Start earning from your empty slots
            </h2>
            <p style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '40px', opacity: 0.95, maxWidth: '500px', margin: '0 auto 40px' }}>
              Zero cost. Zero risk. List your facility today and get your first booking within days.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/gym-survey" style={{ background: '#ffffff', color: '#14B8A6', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                Register Your Facility <ArrowRight size={18} />
              </a>
              <a href="mailto:support@huslrz.com" style={{ background: 'transparent', color: '#ffffff', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '2px solid #ffffff', display: 'inline-flex', alignItems: 'center' }}>
                Email support@huslrz.com
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '64px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px' : '40px', marginBottom: '40px' }}>
              <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#14B8A6', marginBottom: '12px' }}>HUSLRZ</div>
                <p style={{ fontSize: '14px', color: '#9CA3AF', lineHeight: 1.6, margin: 0 }}>India's pay-per-use fitness marketplace. Fitness without commitment.</p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Platform</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/partners" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Partners</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/corporate" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Corporates</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/about" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>About Us</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Legal</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Get in Touch</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px', fontSize: '14px', color: '#9CA3AF' }}>support@huslrz.com</li>
                  <li style={{ fontSize: '14px', color: '#9CA3AF' }}>Chennai, Tamil Nadu</li>
                </ul>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ForPartnersPage;
